import React, { useEffect, useRef } from 'react';

/**
 * CursorEffect, A galactic, success-themed canvas cursor effect.
 * Features:
 *  - Stardust trail particles (comet effect)
 *  - Green success pulse ring
 *  - Orbiting micro-stars
 *  - Faint "200" text badge
 */
const CursorEffect = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;
    let mouse = { x: -200, y: -200 };
    let smoothMouse = { x: -200, y: -200 };
    let particles = [];
    let orbitAngle = 0;
    let pulsePhase = 0;
    let isVisible = false;

    // Resize canvas to fill viewport
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Track mouse
    const onMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      isVisible = true;

      // Spawn trail particles on movement
      const speed = Math.sqrt(
        Math.pow(e.movementX, 2) + Math.pow(e.movementY, 2)
      );
      const count = Math.min(Math.floor(speed / 3), 5);
      for (let i = 0; i < count; i++) {
        spawnTrailParticle(e.clientX, e.clientY, e.movementX, e.movementY);
      }
    };

    const onMouseLeave = () => {
      isVisible = false;
    };

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    document.addEventListener('mouseleave', onMouseLeave);

    // ---- Particle system ----
    function spawnTrailParticle(x, y, vx, vy) {
      const angle = Math.atan2(vy, vx) + (Math.random() - 0.5) * 1.5;
      const speed = 0.5 + Math.random() * 2;
      const size = 3 + Math.random() * 4;
      const life = Math.floor(40 + Math.random() * 30);

      // Color palette: cyan and blue tones for the new theme
      const colors = [
        { r: 6, g: 175, b: 192 },    // Cyan
        { r: 40, g: 57, b: 75 },     // Slate blue
        { r: 255, g: 255, b: 255 },  // White star
      ];
      const color = colors[Math.floor(Math.random() * colors.length)];

      particles.push({
        x: x + (Math.random() - 0.5) * 8,
        y: y + (Math.random() - 0.5) * 8,
        vx: -Math.cos(angle) * speed + (Math.random() - 0.5) * 0.5,
        vy: -Math.sin(angle) * speed + (Math.random() - 0.5) * 0.5,
        size,
        maxLife: life,
        life,
        color,
        type: 'trail',
      });
    }

    // Spawn ambient sparkle particles periodically
    function spawnSparkle() {
      if (!isVisible) return;
      const angle = Math.random() * Math.PI * 2;
      const dist = 70 + Math.random() * 150;
      particles.push({
        x: smoothMouse.x + Math.cos(angle) * dist,
        y: smoothMouse.y + Math.sin(angle) * dist,
        vx: (Math.random() - 0.5) * 0.3,
        vy: -0.2 - Math.random() * 0.5,
        size: 1.5 + Math.random() * 3,
        maxLife: Math.floor(50 + Math.random() * 30),
        life: Math.floor(50 + Math.random() * 30),
        color: { r: 255, g: 255, b: 255 },
        type: 'sparkle',
      });
    }

    // ---- Draw functions ----

    // Draw center success orb
    function drawSuccessOrb(x, y) {
      const pulse = Math.sin(pulsePhase) * 0.3 + 0.7;
      const baseRadius = 26;

      // Inner glow
      const innerGrad = ctx.createRadialGradient(x, y, 0, x, y, baseRadius * 3);
      innerGrad.addColorStop(0, `rgba(6, 175, 192, ${0.3 * pulse})`);
      innerGrad.addColorStop(0.5, `rgba(6, 175, 192, ${0.1 * pulse})`);
      innerGrad.addColorStop(1, 'rgba(6, 175, 192, 0)');
      ctx.beginPath();
      ctx.arc(x, y, baseRadius * 3, 0, Math.PI * 2);
      ctx.fillStyle = innerGrad;
      ctx.fill();

      // Bright core
      const coreGrad = ctx.createRadialGradient(x, y, 0, x, y, baseRadius);
      coreGrad.addColorStop(0, `rgba(6, 175, 192, ${0.5 * pulse})`);
      coreGrad.addColorStop(0.6, `rgba(6, 175, 192, ${0.3 * pulse})`);
      coreGrad.addColorStop(1, 'rgba(6, 175, 192, 0)');
      ctx.beginPath();
      ctx.arc(x, y, baseRadius, 0, Math.PI * 2);
      ctx.fillStyle = coreGrad;
      ctx.fill();
    }

    // Draw success pulse ring
    function drawPulseRing(x, y) {
      const pulse = Math.sin(pulsePhase * 0.7) * 0.5 + 0.5;
      const radius = 80 + pulse * 50;
      const alpha = 0.08 + pulse * 0.08;

      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(6, 175, 192, ${alpha})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Second ring expanding slower
      const pulse2 = Math.sin(pulsePhase * 0.4 + 1) * 0.5 + 0.5;
      const radius2 = 130 + pulse2 * 60;
      const alpha2 = 0.04 + pulse2 * 0.05;
      ctx.beginPath();
      ctx.arc(x, y, radius2, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(40, 57, 75, ${alpha2})`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Draw orbiting micro-stars
    function drawOrbitals(x, y) {
      const orbitalCount = 4;
      for (let i = 0; i < orbitalCount; i++) {
        const angle = orbitAngle + (i * Math.PI * 2) / orbitalCount;
        const orbitRadius = 110 + Math.sin(pulsePhase + i) * 20;
        const ox = x + Math.cos(angle) * orbitRadius;
        const oy = y + Math.sin(angle) * orbitRadius;
        const size = Math.max(0, 5 + Math.sin(pulsePhase * 2 + i) * 2.5);

        // Star glow
        const starGrad = ctx.createRadialGradient(ox, oy, 0, ox, oy, size * 3);
        const colors = [
          'rgba(6, 175, 192, 0.3)',
          'rgba(40, 57, 75, 0.3)',
          'rgba(40, 57, 75, 0.3)',
          'rgba(255, 255, 255, 0.4)',
        ];
        starGrad.addColorStop(0, colors[i]);
        starGrad.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.arc(ox, oy, size * 3, 0, Math.PI * 2);
        ctx.fillStyle = starGrad;
        ctx.fill();

        // Star core
        ctx.beginPath();
        ctx.arc(ox, oy, size, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.fill();
      }
    }

    // Draw the "200" text badge
    function drawStatusBadge(x, y) {
      const badgeX = x + 90;
      const badgeY = y - 70;
      const alpha = 0.2 + Math.sin(pulsePhase * 0.5) * 0.1;

      ctx.font = '600 16px "JetBrains Mono", monospace';
      ctx.fillStyle = `rgba(6, 175, 192, ${alpha})`;
      ctx.textAlign = 'left';
      ctx.fillText('200', badgeX, badgeY);

      // Tiny checkmark after "200"
      ctx.font = '600 12px "JetBrains Mono", monospace';
      ctx.fillStyle = `rgba(6, 175, 192, ${alpha * 0.8})`;
      ctx.fillText(' OK', badgeX + 32, badgeY);
    }

    // Draw galactic ambient glow around cursor
    function drawNebulaGlow(x, y) {
      const radius = 600;
      const nebulaGrad = ctx.createRadialGradient(x, y, 0, x, y, radius);
      nebulaGrad.addColorStop(0, 'rgba(6, 175, 192, 0.02)');
      nebulaGrad.addColorStop(0.3, 'rgba(40, 57, 75, 0.015)');
      nebulaGrad.addColorStop(0.6, 'rgba(40, 57, 75, 0.01)');
      nebulaGrad.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = nebulaGrad;
      ctx.fill();
    }

    // ---- Main animation loop ----
    let sparkleTimer = 0;

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Smooth mouse follow (slight lag for organic feel)
      smoothMouse.x += (mouse.x - smoothMouse.x) * 0.15;
      smoothMouse.y += (mouse.y - smoothMouse.y) * 0.15;

      // Update phases
      orbitAngle += 0.025;
      pulsePhase += 0.05;
      sparkleTimer++;

      // Spawn ambient sparkles
      if (sparkleTimer % 4 === 0) {
        spawnSparkle();
      }

      // Update and draw particles
      particles = particles.filter((p) => p.life > 0);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.life--;
        p.vx *= 0.98;
        p.vy *= 0.98;

        const progress = Math.max(0, p.life / p.maxLife);
        const alpha = progress * 0.15;
        const size = Math.max(0, p.size * progress);

        if (p.type === 'sparkle') {
          // Twinkle effect
          const twinkle = Math.sin(p.life * 0.5) * 0.5 + 0.5;
          const sparkleAlpha = alpha * twinkle;

          // Star shape = 4-point glow
          const sg = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, size * 2);
          sg.addColorStop(0, `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${sparkleAlpha})`);
          sg.addColorStop(1, 'transparent');
          ctx.beginPath();
          ctx.arc(p.x, p.y, size * 2, 0, Math.PI * 2);
          ctx.fillStyle = sg;
          ctx.fill();
        } else {
          // Trail particles with glow
          const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, size * 2.5);
          glow.addColorStop(0, `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${alpha})`);
          glow.addColorStop(0.4, `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${alpha * 0.4})`);
          glow.addColorStop(1, 'transparent');
          ctx.beginPath();
          ctx.arc(p.x, p.y, size * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = glow;
          ctx.fill();

          // Bright core
          ctx.beginPath();
          ctx.arc(p.x, p.y, size * 0.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.3})`;
          ctx.fill();
        }
      }

      if (isVisible) {
        const sx = smoothMouse.x;
        const sy = smoothMouse.y;

        // Draw layers from back to front
        drawNebulaGlow(sx, sy);
        drawPulseRing(sx, sy);
        drawOrbitals(sx, sy);
        drawSuccessOrb(sx, sy);
        drawStatusBadge(sx, sy);
      }

      animationId = requestAnimationFrame(animate);
    }

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    />
  );
};

export default CursorEffect;
